import styled from "@emotion/styled";
import { ThemeMode } from "@smartsheet/sds-theme-internal";
import { layers } from "../../../constants/layers";
import { WIDTH_ENUM, gutter, WidthNames } from "../shared-variables";
import { modalTheme } from "./modalTheme";

const maxWidthDimensions = `calc(100% - ${gutter * 2}px)`;

const maxHeightDimensions = (gutterHeight: string | number | undefined): string => {
    if (typeof gutterHeight === "string") {
        const percent = parseInt(gutterHeight);

        return `${100 - percent * 2}%`;
    }

    const gutterHeightPx = typeof gutterHeight === "number" ? gutterHeight : gutter;

    return `calc(100% - ${gutterHeightPx * 2}px)`;
};

interface ModalWidthOptions {
    widthName: WidthNames | undefined;
    widthValue: string | number | undefined;
}

export const modalWidth = ({ widthName, widthValue }: ModalWidthOptions) => {
    if (typeof widthValue === "number") {
        return `${widthValue}px`;
    }

    return widthName ? `${WIDTH_ENUM.widths[widthName]}px` : widthValue || "auto";
};

export const modalHeight = ({ heightValue }: { heightValue: string | number | undefined }) => {
    if (typeof heightValue === "number") {
        return `${heightValue}px`;
    }
    return heightValue || "auto";
};

const positionerAbsoluteTop = (gutterHeight: string | number | undefined): string => {
    if (typeof gutterHeight === "string") {
        return gutterHeight;
    }

    const gutterHeightPx = typeof gutterHeight === "number" ? gutterHeight : gutter;

    return `${gutterHeightPx}px`;
};

export const FillScreen = styled("div")`
    height: 100vh;
    left: 0;
    overflow-y: auto;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: ${layers.modal};
    -webkit-overflow-scrolling: touch;
`;
FillScreen.displayName = "StyledFillScreen";

interface PositionerAbsolute {
    gutterHeight: number | string | undefined;
    placement: "top" | "center";
}

export const PositionerAbsolute = styled("div")<PositionerAbsolute>`
    position: absolute;
    top: ${({ gutterHeight }) => positionerAbsoluteTop(gutterHeight)};
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    justify-content: ${({ placement }) => (placement === "center" ? "center" : "flex-start")};
    height: ${({ gutterHeight }) => maxHeightDimensions(gutterHeight)};
    max-width: ${maxWidthDimensions};
    margin-left: auto;
    margin-right: auto;
    z-index: ${layers.modal};
    pointer-events: none;

    @media (min-width: 320px) and (max-width: 480px) {
        position: fixed;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        max-width: 100%;
    }
`;
PositionerAbsolute.displayName = "StyledPositionerAbsolute";

export interface StyledModalContainerProps {
    themeMode: ThemeMode;
    heightValue?: string | number;
}

export const StyledModalContainer = styled("div")<StyledModalContainerProps>`
    background-color: ${({ themeMode }) => modalTheme.backgroundColor(themeMode)};
    border-radius: ${({ themeMode, heightValue }) =>
        heightValue === "100vh" ? "0px" : modalTheme.borderRadius(themeMode)};
    box-shadow: ${({ themeMode }) => modalTheme.boxShadow(themeMode)};
    color: ${({ themeMode }) => modalTheme.color(themeMode)};
    display: flex;
    flex-direction: column;
    height: ${({ heightValue }) => modalHeight({ heightValue })};
    max-height: 100%;
    min-height: 264px;

    /* the parent (PositionerAbsolute) turns off pointer events, so we need to turn it back on */
    pointer-events: auto;

    @media (min-width: 320px) and (max-width: 480px) {
        height: 100%;
        max-height: 100%;
        border-radius: 0;
    }
`;

StyledModalContainer.displayName = "StyledModalContainer";
